// src/tour/components/TourLauncher.js
//
// Panel listing the tours a user can start or replay. v1 lists only the
// Welcome tour; the layout is built for N. Shows each tour's name, estimated
// time, status (not started / in progress / completed), and a Start/Restart
// button. Below the list: a placeholder Help & FAQ link (the searchable help
// index is v1.1).

import { useEffect, useState, useCallback } from 'react'
import { supabase } from '../../supabase'
import { listAvailableTours } from '../scripts'
import { useTour } from '../useTour'
import { shouldFireOnManualLaunch } from '../triggers'
import { TC, touchBtn } from './tourTheme'

function statusFor(progress) {
  if (!progress) return { key: 'not_started', label: 'Not started' }
  if (progress.completed_at) return { key: 'completed', label: 'Completed' }
  if (progress.dismissed_at) return { key: 'not_started', label: 'Not started' }
  if (progress.started_at) return { key: 'in_progress', label: 'In progress' }
  return { key: 'not_started', label: 'Not started' }
}

export default function TourLauncher({ session, homeId = null, onClose }) {
  const { actions } = useTour()
  const [progressByTour, setProgressByTour] = useState({})
  const tours = listAvailableTours()
  const userId = session?.user?.id

  useEffect(() => {
    // launcher_opened analytics (best-effort, not tied to an active tour)
    if (userId) {
      supabase.from('tour_events').insert({
        user_id: userId, tour_id: '*', home_id: homeId, event_type: 'launcher_opened',
      }).then(({ error }) => { if (error) console.warn('[tour] launcher_opened log failed:', error.message) })
    }
  }, [userId, homeId])

  useEffect(() => {
    let cancelled = false
    async function load() {
      if (!userId) return
      const { data, error } = await supabase
        .from('tour_progress')
        .select('tour_id, started_at, completed_at, dismissed_at')
        .eq('user_id', userId)
      if (cancelled || error) {
        if (error) console.warn('[tour] launcher progress read failed:', error.message)
        return
      }
      const map = {}
      for (const row of data || []) map[row.tour_id] = row
      setProgressByTour(map)
    }
    load()
    return () => { cancelled = true }
  }, [userId])

  const handleStart = useCallback(async (tourId) => {
    if (userId) {
      supabase.from('tour_events').insert({
        user_id: userId, tour_id: tourId, home_id: homeId, event_type: 'launcher_tour_clicked',
      }).then(({ error }) => { if (error) console.warn('[tour] launcher_tour_clicked log failed:', error.message) })
    }
    const allowed = await shouldFireOnManualLaunch(userId, tourId)
    if (!allowed) return
    onClose?.()
    actions.startTour(tourId, homeId)
  }, [actions, userId, homeId, onClose])

  return (
    <div style={{ fontFamily: TC.fBody, color: TC.body }}>
      <div style={{ fontFamily: TC.fHead, fontSize: 20, marginBottom: 4 }}>Help &amp; Tours</div>
      <div style={{ fontSize: 13.5, color: TC.muted, marginBottom: 18 }}>
        Take a guided walkthrough any time.
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        {tours.map(t => {
          const st = statusFor(progressByTour[t.id])
          const isRestart = st.key === 'completed' || st.key === 'in_progress'
          return (
            <div
              key={t.id}
              style={{
                border: `1px solid ${TC.bubbleBorder}`,
                borderRadius: 12,
                padding: 16,
                background: TC.bubbleBg,
                display: 'flex',
                alignItems: 'center',
                gap: 12,
              }}
            >
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontFamily: TC.fHead, fontSize: 16 }}>{t.name}</div>
                <div style={{ fontSize: 12.5, color: TC.muted, marginTop: 3 }}>
                  ~{t.estimatedMinutes} min · {t.endsWith}
                </div>
                <div style={{
                  display: 'inline-block', marginTop: 8, fontSize: 11.5, fontWeight: 600,
                  color: st.key === 'completed' ? TC.success : TC.muted,
                  background: st.key === 'completed' ? TC.successBg : 'transparent',
                  padding: st.key === 'completed' ? '2px 8px' : 0,
                  borderRadius: 6,
                }}>
                  {st.label}
                </div>
              </div>
              <button
                onClick={() => handleStart(t.id)}
                style={{
                  ...touchBtn, border: 'none', background: TC.primary, color: '#fff', fontWeight: 600,
                  whiteSpace: 'nowrap',
                }}
              >
                {isRestart ? 'Restart' : 'Start'}
              </button>
            </div>
          )
        })}
      </div>

      <button
        onClick={() => { /* v1.1: searchable help index */ }}
        style={{
          marginTop: 18, background: 'none', border: 'none', color: TC.primary,
          fontFamily: TC.fBody, fontSize: 14, cursor: 'pointer', padding: 0,
        }}
        title="Coming soon"
      >
        Help &amp; FAQ &rarr;
      </button>
    </div>
  )
}
